// HM-310.r cluster 15 visual rework: sidebar matched a
// docs/design-references/dashboard-v2-reference.html.
// 240px expanded / 64px collapsed (breakpoint 900px). Header: logo
// HM arancio + nome hotel + location. Voce attiva: bg #5C4632 + border-left
// 3px arancio + icona arancio. User-card sempre in fondo (mt-auto) con
// avatar + nome + ruolo + LogOut bottone.

import { Tooltip, TooltipContent, TooltipTrigger } from './components/Tooltip';
import { ChevronLeft, LogOut, type LucideIcon } from './icons';
import { cn } from './lib/cn';

export interface SidebarItem {
  id: string;
  label: string;
  href: string;
  icon: LucideIcon;
}

export interface SidebarUser {
  name: string;
  role?: string;
}

export interface SidebarHotel {
  name: string;
  location?: string;
}

export interface SidebarProps {
  hotel: SidebarHotel;
  user: SidebarUser;
  items: SidebarItem[];
  activeItemId: string;
  onItemSelect: (id: string) => void;
  onLogout?: () => void;
  mode: 'expanded' | 'collapsed';
  // HM-600 iPad UX: toggle manuale espandi/comprimi (opzionale).
  onToggle?: () => void;
}

export function Sidebar(props: SidebarProps) {
  const collapsed = props.mode === 'collapsed';

  return (
    <aside
      data-sidebar-mode={props.mode}
      className={cn(
        // HM-316.r cluster 15.4: position fixed left/top per restare ferma
        // durante lo scroll del main content (mockup v3 sidebar fissa).
        // z-40: sopra il content, sotto i Dialog overlay (z-50 della Radix).
        'fixed top-0 left-0 h-[var(--app-vh,100dvh)] z-40 bg-bg-sidebar flex flex-col flex-shrink-0',
        collapsed ? 'w-16 px-2 py-4' : 'w-60 px-3 py-5',
      )}
    >
      <SidebarHeader hotel={props.hotel} collapsed={collapsed} onToggle={props.onToggle} />

      {/* QA sweep 2026-07-22 (bug ALTO iPad 1376x1032): la sidebar
          h-screen NON scrollava e la user card (Esci) finiva fuori
          schermo. Scrolla SOLO la nav centrale (flex-1 min-h-0
          overflow-y-auto); header e user card restano sempre visibili. */}
      <nav className="flex flex-col gap-1 flex-1 min-h-0 overflow-y-auto">
        {props.items.map((item) => (
          <NavItem
            key={item.id}
            item={item}
            active={item.id === props.activeItemId}
            onSelect={props.onItemSelect}
            collapsed={collapsed}
          />
        ))}
      </nav>

      <UserCard user={props.user} onLogout={props.onLogout} collapsed={collapsed} />
    </aside>
  );
}

function SidebarHeader({
  hotel,
  collapsed,
  onToggle,
}: {
  hotel: SidebarHotel;
  collapsed: boolean;
  onToggle?: () => void;
}) {
  return (
    <div
      className={cn(
        // shrink-0: l'header non si comprime quando la nav scrolla (QA
        // sweep 2026-07-22).
        'flex items-center gap-3 pb-5 mb-3 border-b border-border-sidebar shrink-0',
        collapsed ? 'flex-col gap-2 pb-3 mb-2' : 'px-3',
      )}
    >
      <div className="w-9 h-9 rounded-[10px] bg-accent-primary flex items-center justify-center text-text-on-accent font-bold text-sm flex-shrink-0">
        HM
      </div>
      {!collapsed && (
        <div className="min-w-0">
          <div className="text-text-on-sidebar font-semibold text-sm truncate">{hotel.name}</div>
          {hotel.location && (
            <div className="text-text-on-sidebar-muted text-[11px] mt-0.5 truncate">
              {hotel.location}
            </div>
          )}
        </div>
      )}
      {/* HM-600 iPad UX: toggle espandi/comprimi. Tap target >=44px,
          aria-expanded riflette lo stato. In expanded sta a destra
          (ml-auto), in collapsed sotto il logo. */}
      {onToggle && (
        <button
          type="button"
          onClick={onToggle}
          aria-label={collapsed ? 'Espandi menu' : 'Comprimi menu'}
          aria-expanded={!collapsed}
          className={cn(
            'flex items-center justify-center h-11 w-11 min-h-[44px] min-w-[44px] rounded-lg',
            'text-text-on-sidebar-muted hover:bg-bg-sidebar-hover hover:text-text-on-sidebar transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary',
            collapsed ? '' : 'ml-auto -mr-1',
          )}
        >
          <ChevronLeft
            size={18}
            strokeWidth={1.75}
            className="transition-transform"
            style={{ transform: collapsed ? 'rotate(180deg)' : 'none' }}
          />
        </button>
      )}
    </div>
  );
}

function NavItem({
  item,
  active,
  onSelect,
  collapsed,
}: {
  item: SidebarItem;
  active: boolean;
  onSelect: (id: string) => void;
  collapsed: boolean;
}) {
  const Icon = item.icon;

  const button = (
    <button
      type="button"
      onClick={() => onSelect(item.id)}
      data-active={active}
      aria-current={active ? 'page' : undefined}
      aria-label={collapsed ? item.label : undefined}
      className={cn(
        'flex items-center gap-3 rounded-lg text-sm transition-colors',
        'border-l-[3px] border-transparent',
        // padding-left compensa il border 3px cosi' attivazione non sposta
        collapsed ? 'justify-center py-2.5 pl-3 pr-3' : 'py-2.5 pl-[9px] pr-3',
        'text-text-on-sidebar-muted hover:bg-bg-sidebar-hover hover:text-text-on-sidebar',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary focus-visible:ring-offset-0',
        active && 'bg-bg-sidebar-active text-text-on-sidebar !border-l-accent-primary',
      )}
    >
      <Icon
        size={18}
        strokeWidth={1.75}
        className={active ? 'text-accent-primary shrink-0' : 'shrink-0'}
      />
      {!collapsed && <span>{item.label}</span>}
    </button>
  );

  if (!collapsed) return button;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side="right">{item.label}</TooltipContent>
    </Tooltip>
  );
}

function UserCard({
  user,
  onLogout,
  collapsed,
}: {
  user: SidebarUser;
  onLogout?: () => void;
  collapsed: boolean;
}) {
  const initial = (user.name?.charAt(0) ?? '?').toUpperCase();

  if (collapsed) {
    return (
      // shrink-0 + mt-auto: SEMPRE visibile in fondo, fuori dallo scroll
      // della nav (QA sweep 2026-07-22: Esci irraggiungibile su iPad).
      <div className="mt-auto pt-3 shrink-0">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={onLogout}
              aria-label={`Esci (${user.name})`}
              className="w-full bg-[#2A1F18] rounded-[10px] p-2.5 flex items-center justify-center hover:bg-bg-sidebar-active transition-colors"
            >
              <span className="w-9 h-9 rounded-full bg-accent-primary flex items-center justify-center text-text-on-accent font-semibold text-[13px]">
                {initial}
              </span>
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">{user.name} · clic per uscire</TooltipContent>
        </Tooltip>
      </div>
    );
  }

  return (
    // shrink-0 + mt-auto: SEMPRE visibile in fondo, fuori dallo scroll
    // della nav (QA sweep 2026-07-22: Esci irraggiungibile su iPad).
    <div className="mt-auto pt-3 shrink-0">
      <div className="bg-[#2A1F18] rounded-[10px] p-3 flex items-center gap-2.5">
        <span className="w-9 h-9 rounded-full bg-accent-primary flex items-center justify-center text-text-on-accent font-semibold text-[13px] flex-shrink-0">
          {initial}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-text-on-sidebar text-[13px] font-medium truncate">{user.name}</div>
          {user.role && (
            <div className="text-text-on-sidebar-muted text-[11px] mt-0.5 truncate">
              {user.role}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onLogout}
          aria-label="Esci"
          className="text-text-on-sidebar-muted hover:text-text-on-sidebar p-1 rounded transition-colors"
        >
          <LogOut size={16} strokeWidth={1.75} />
        </button>
      </div>
    </div>
  );
}
